/**
 * Catalog data layer for the Coral Placer — pure, dependency-free helpers
 * that load and normalize the bundled coral catalog, merge operator edits,
 * and filter/sort the result. Kept free of React/DOM so every function is
 * unit-testable in isolation.
 */
import { piAssetUrl } from '../lib/env';
import {
  COLOR_TAGS,
  GROWTH_FORMS,
  type Coral,
  type CoralEdit,
  type CoralEdits,
  type ColorTag,
  type FilterState,
  type GrowthForm,
  type PlacementPhoto,
  type SortKey,
} from './types';
import coralsJson from './data/corals.json';

/**
 * Base URL for the Pi-hosted coral photos. Resolved through `piAssetUrl`
 * so a Vercel-hosted app loads them from the Pi's origin, while a
 * Pi-served page keeps the relative path.
 * Note: this deliberately lives under `/coral-assets/`, NOT `/coral-placer/`,
 * so the static photo directory never collides with the SPA route
 * `/coral-placer` (a real `coral-placer/` dir made nginx 301-redirect the
 * route to a directory listing instead of serving the app).
 */
export const PHOTO_BASE = piAssetUrl('/coral-assets/photos');

/** Display color for each color tag (used by chips, dots, and the mind map). */
export const COLOR_HEX: Record<ColorTag, string> = {
  YELLOW: '#eab308',
  WARM: '#f97316',
  BLUE: '#3b82f6',
  PURPLE: '#a855f7',
  GREEN: '#22c55e',
  PINK: '#ec4899',
  MULTI: '#8b5cf6',
};

export function isColorTag(v: unknown): v is ColorTag {
  return typeof v === 'string' && (COLOR_TAGS as readonly string[]).includes(v);
}

export function isGrowthForm(v: unknown): v is GrowthForm {
  return (
    typeof v === 'string' && (GROWTH_FORMS as readonly string[]).includes(v)
  );
}

/**
 * Strip Markdown link syntax (`[label](url)`) down to just the label and
 * trim trailing source-citation lists, so descriptive fields render as
 * clean prose instead of raw Markdown.
 */
export function cleanText(raw: string): string {
  return raw
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

/** Normalize one raw JSON entry into a typed `Coral`. */
function normalizeCoral(raw: unknown): Coral | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = asString(o['id']);
  const name = asString(o['name']);
  if (id === '' || name === '') return null;

  const colorsRaw = Array.isArray(o['colors']) ? o['colors'] : [];
  const colors = colorsRaw.filter(isColorTag);
  const growthForm = isGrowthForm(o['growth_form']) ? o['growth_form'] : 'BUSHY';

  return {
    id,
    name,
    species: asString(o['species'], 'Acropora sp.'),
    growthFormRaw: asString(o['growth_form_raw']),
    bodyColor: asString(o['body_color']),
    polypColor: asString(o['polyp_color']),
    fragPhoto: asString(o['frag_photo']),
    colonyPhoto: asString(o['colony_photo']),
    tsaUrl: asString(o['tsa_url']),
    colors: colors.length > 0 ? colors : ['MULTI'],
    growthForm,
  };
}

/** Load and normalize the full bundled catalog. */
export function loadCatalog(): Coral[] {
  const arr: unknown = coralsJson;
  if (!Array.isArray(arr)) return [];
  const out: Coral[] = [];
  for (const entry of arr) {
    const coral = normalizeCoral(entry);
    if (coral) out.push(coral);
  }
  return out;
}

/** Merge a single coral with its operator edit overlay (if any). */
export function mergeCoral(base: Coral, edit: CoralEdit | undefined): Coral {
  if (!edit) return base;
  return {
    ...base,
    name: edit.name ?? base.name,
    species: edit.species ?? base.species,
    bodyColor: edit.bodyColor ?? base.bodyColor,
    polypColor: edit.polypColor ?? base.polypColor,
    tsaUrl: edit.tsaUrl ?? base.tsaUrl,
    colors:
      edit.colors && edit.colors.length > 0 ? edit.colors : base.colors,
    growthForm: edit.growthForm ?? base.growthForm,
  };
}

/** Apply the full edits overlay across a catalog. */
export function applyEdits(corals: readonly Coral[], edits: CoralEdits): Coral[] {
  return corals.map((c) => mergeCoral(c, edits[c.id]));
}

/** Full URL for a coral's frag photo. */
export function fragPhotoUrl(coral: Coral): string {
  return `${PHOTO_BASE}/frags/${coral.fragPhoto}`;
}

/** Full URL for a coral's colony photo. */
export function colonyPhotoUrl(coral: Coral): string {
  return `${PHOTO_BASE}/colonies/${coral.colonyPhoto}`;
}

/** Full URL for whichever photo a placement displays. */
export function placementPhotoUrl(coral: Coral, which: PlacementPhoto): string {
  return which === 'frag' ? fragPhotoUrl(coral) : colonyPhotoUrl(coral);
}

/** Case-insensitive name/species substring match. */
function matchesQuery(coral: Coral, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  return (
    coral.name.toLowerCase().includes(q) ||
    coral.species.toLowerCase().includes(q)
  );
}

/** Filter a catalog by the active search/color/growth-form filters. */
export function filterCorals(
  corals: readonly Coral[],
  filters: Pick<FilterState, 'query' | 'colors' | 'growthForms'>,
): Coral[] {
  return corals.filter((coral) => {
    if (!matchesQuery(coral, filters.query)) return false;
    if (
      filters.colors.length > 0 &&
      !coral.colors.some((c) => filters.colors.includes(c))
    ) {
      return false;
    }
    if (
      filters.growthForms.length > 0 &&
      !filters.growthForms.includes(coral.growthForm)
    ) {
      return false;
    }
    return true;
  });
}

function colorRank(coral: Coral): number {
  const first = coral.colors[0];
  if (!first) return COLOR_TAGS.length;
  const idx = COLOR_TAGS.indexOf(first);
  return idx === -1 ? COLOR_TAGS.length : idx;
}

function growthRank(coral: Coral): number {
  const idx = GROWTH_FORMS.indexOf(coral.growthForm);
  return idx === -1 ? GROWTH_FORMS.length : idx;
}

/** Sort a catalog by the chosen key (stable, non-mutating). */
export function sortCorals(corals: readonly Coral[], sort: SortKey): Coral[] {
  const out = [...corals];
  switch (sort) {
    case 'name-asc':
      out.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'name-desc':
      out.sort((a, b) => b.name.localeCompare(a.name));
      break;
    case 'growth':
      out.sort(
        (a, b) => growthRank(a) - growthRank(b) || a.name.localeCompare(b.name),
      );
      break;
    case 'color':
      out.sort(
        (a, b) => colorRank(a) - colorRank(b) || a.name.localeCompare(b.name),
      );
      break;
  }
  return out;
}

/** Convenience: filter then sort in one call. */
export function filterAndSort(
  corals: readonly Coral[],
  filters: FilterState,
): Coral[] {
  return sortCorals(filterCorals(corals, filters), filters.sort);
}

/** Look up a coral by id within a catalog. */
export function findCoral(
  corals: readonly Coral[],
  id: string,
): Coral | undefined {
  return corals.find((c) => c.id === id);
}
